import { useAppDispatch, useAppSelector } from 'app/hooks';
import Slider from 'components/inputs/slider';
import InfoTooltip from 'components/layout/info-tooltip';
import { updateProjectSetting } from 'features/project';
import { setGalleryPage } from 'features/ui';
import { useMemo } from 'react';
import {
  categoryToProjectSettingCode,
  categoryWeightLabels,
  CategoryWeightLevel,
  categoryWeightLevelsFromProjectSettings,
  orderedWeightedCategories,
  WeightedCategoryCode
} from 'utils/system-gallery-filters';

const LEVEL_BY_INDEX: CategoryWeightLevel[] = ['Low', 'Medium', 'High'];
const INDEX_BY_LEVEL: Record<CategoryWeightLevel, number> = {
  Low: 0,
  Medium: 1,
  High: 2
};

// The Customer Priorities sliders: the category multipliers the ductless
// gallery scores with. They apply to the ductless gallery only — the ducted
// path orders its pool by the Sort by dropdown — so this section lives in
// the side panel, which the compatibility pathway hides.
export default function CustomerPrioritiesSection() {
  const dispatch = useAppDispatch();
  const projectSettings = useAppSelector(
    (state) => state.project.projectSettings
  );
  const attributes = useAppSelector((state) => state.data.attributes);
  const tooltipByCode = useMemo(() => {
    const map = new Map<string, string>();
    for (const attr of attributes) {
      if (attr.tooltip && attr.tooltip.length > 0) {
        map.set(attr.code, attr.tooltip);
      }
    }
    return map;
  }, [attributes]);
  const categoryWeights =
    categoryWeightLevelsFromProjectSettings(projectSettings);

  const isModified = orderedWeightedCategories.some((cat) => {
    const code = categoryToProjectSettingCode[cat];
    const raw = projectSettings.find((ps) => ps.code === code)?.value;
    return typeof raw === 'string' && raw.length > 0 && raw !== 'Medium';
  });

  const handleChange = (
    category: WeightedCategoryCode,
    sliderValue: number
  ) => {
    const idx = Math.max(0, Math.min(2, Math.round(sliderValue)));
    const level = LEVEL_BY_INDEX[idx];
    dispatch(
      updateProjectSetting({
        attributeCode: categoryToProjectSettingCode[category],
        value: level
      })
    );
    dispatch(setGalleryPage(0));
  };

  const handleReset = () => {
    orderedWeightedCategories.forEach((cat) => {
      dispatch(
        updateProjectSetting({
          attributeCode: categoryToProjectSettingCode[cat],
          value: undefined
        })
      );
    });
    dispatch(setGalleryPage(0));
  };

  return (
    <div className='flex flex-col gap-4'>
      <div className='text-xl font-bold text-gray-900'>Customer Priorities</div>
      <div className='flex flex-col gap-4'>
        {orderedWeightedCategories.map((category) => {
          const level = categoryWeights[category];
          const idx = INDEX_BY_LEVEL[level];
          const htmlId = `customer-preference-${category}`;
          const tooltipText = tooltipByCode.get(
            categoryToProjectSettingCode[category]
          );
          return (
            <div key={category} className='flex flex-col gap-1'>
              <div className='flex items-center gap-1'>
                <label
                  className='text-sm font-medium text-gray-700'
                  htmlFor={htmlId}
                >
                  {categoryWeightLabels[category]}
                </label>
                {tooltipText && (
                  <InfoTooltip>
                    <span>{tooltipText}</span>
                  </InfoTooltip>
                )}
              </div>
              <Slider
                value={idx}
                min={0}
                max={2}
                onChange={(v) => handleChange(category, v)}
              />
              <div className='flex w-full items-center justify-between text-xs text-gray-500'>
                <span>Less important</span>
                <span>More important</span>
              </div>
            </div>
          );
        })}
      </div>
      {isModified && (
        <button
          type='button'
          onClick={handleReset}
          className='self-start text-sm font-medium text-blue-600 hover:text-blue-800'
        >
          Reset Priorities
        </button>
      )}
    </div>
  );
}
